import { ReactNode, ReactElement, ElementType } from "react";
import { useIntl } from "react-intl";
import { makeStyles } from "tss-react/mui";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  SxProps,
  Theme,
  Typography,
  Tooltip,
  TooltipProps,
  IconButton,
} from "@mui/material";

interface ActionTooltipProps extends TooltipProps {
  children: ReactElement;
  title: string | ReactNode;
  translateTitle?: boolean;
  rawChildren?: boolean;
  nancieTooltip?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  tooltip: {
    display: "flex",
  },
}));

/**
 * Generic Tooltip
 *
 * @param title Text of tooltip
 * @param translateTitle should translate title props with intl, true by default
 * @param rawChildren should display children props without span wrapper
 */
export const ActionTooltip = ({
  children,
  title,
  translateTitle = true,
  rawChildren,
  nancieTooltip,
  ...props
}: ActionTooltipProps) => {
  const intl = useIntl();

  return (
    <Tooltip
      title={
        translateTitle && title && typeof title === "string"
          ? intl.formatMessage({ id: title }, { br: <br /> })
          : title
      }
      slotProps={{
        tooltip: {
          sx: (theme) =>
            nancieTooltip
              ? {
                  backgroundColor: theme.vars.palette.nancie.main,
                  borderRadius: "6px",
                  boxShadow: theme.shadows[3],
                  color: theme.vars.palette.common.white,
                  fontSize: "0.8125rem",
                  padding: theme.spacing(1),
                  "& *": {
                    color: theme.vars.palette.common.white,
                  },
                }
              : {},
        },
      }}
      {...props}
    >
      {rawChildren ? (
        children
      ) : (
        <span style={{ alignContent: "center" }}>{children}</span>
      )}
    </Tooltip>
  );
};

/**
 * Generic tooltip for input label
 *
 * @param label
 * @param labelComponent
 * @param tooltipLabel
 * @param sxStyles
 * @param translateTooltip
 */
export const TooltipLabel = ({
  label,
  labelComponent = "span",
  tooltipLabel,
  sxStyles,
  translateTooltip = true,
}: {
  label: string | ReactNode;
  labelComponent?: ElementType;
  tooltipLabel: string | ReactNode;
  translateTooltip?: boolean;
  sxStyles?: SxProps<Theme>;
}) => {
  const { classes } = useStyles();

  return (
    <Typography
      component={labelComponent}
      className={classes.tooltip}
      sx={{ fontSize: "0.8125rem", fontWeight: "inherit", ...sxStyles }}
    >
      {label}
      <ActionTooltip
        title={tooltipLabel}
        translateTitle={translateTooltip}
        nancieTooltip
      >
        <InfoIcon
          className="nancieIcon"
          fontSize="small"
          sx={{
            color: "nancie.main",
            ml: 0.625,
            verticalAlign: "middle",
            display: "block",
          }}
        />
      </ActionTooltip>
    </Typography>
  );
};

/**
 * Static info text — icon + text always visible, no hover required
 *
 * @param text
 * @param translateText - translate with intl, true by default
 */
export const InfoText = ({
  text,
  translateText = true,
  sxProps,
}: {
  text: string;
  translateText?: boolean;
  sxProps?: SxProps;
}) => {
  const intl = useIntl();
  return (
    <Box sx={{ display: "flex", gap: 1, ...sxProps }}>
      <InfoIcon fontSize="small" sx={{ color: "nancie.main" }} />
      <Typography sx={{ color: "nancie.main", fontSize: "0.8125rem" }}>
        {translateText
          ? intl.formatMessage({ id: text }, { br: <br /> })
          : text}
      </Typography>
    </Box>
  );
};

/**
 * Generic info icon with tooltip on hover
 *
 * @param title - text of the tooltip
 * @param translateTitle - translate title or not
 * @param sxStyles
 * @returns
 */
export const InfoButton = ({
  title,
  translateTitle = true,
  sxStyles,
}: {
  title: string | ReactNode;
  translateTitle?: boolean;
  sxStyles?: SxProps;
}) => {
  return (
    <ActionTooltip title={title} translateTitle={translateTitle} nancieTooltip>
      <IconButton size="small" sx={{ ...sxStyles }}>
        <InfoIcon fontSize="small" sx={{ color: "nancie.main" }} />
      </IconButton>
    </ActionTooltip>
  );
};
